import { useState, useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/services/api'
import { MarketCard } from '@/components/MarketCard'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { Search, Filter, TrendingUp, Menu, X, Home, BarChart, User, LogOut, BarChart3 } from 'lucide-react'
import { Card } from '@/components/ui/card'
import { useAuthStore } from '@/store/authStore'
import { useNavigate, Link } from 'react-router-dom'
import gsap from 'gsap'

export function Markets() {
  const [search, setSearch] = useState('')
  const [category, setCategory] = useState<string>('')
  const [menuOpen, setMenuOpen] = useState(false)
  const [showAnalytics, setShowAnalytics] = useState(false)
  const { user, isGuest, logout } = useAuthStore()
  const navigate = useNavigate()

  const { data } = useQuery({
    queryKey: ['markets', { search, category }],
    queryFn: () =>
      apiClient.getMarkets({
        page: 1,
        pageSize: 20,
        search: search || undefined,
        category: category || undefined,
      }),
  })

  const categories = ['Technology', 'Crypto']

  const handleLogout = async () => {
    await logout()
    navigate('/')
  }

  useEffect(() => {
    if (menuOpen) {
      gsap.fromTo('.menu-item',
        { x: -50, opacity: 0 },
        { x: 0, opacity: 1, stagger: 0.1, duration: 0.3, ease: 'power2.out' }
      )
    }
  }, [menuOpen])

  return (
    <div className="relative min-h-screen">
      {/* Background Image */}
      <div className="fixed inset-0 -z-10 overflow-hidden pointer-events-none">
        <div
          className="absolute inset-0 w-full h-full bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: 'url(https://t4.ftcdn.net/jpg/09/17/32/77/360_F_917327795_YLhmTQeANcRigtyMjthLfxotWKyK3vzH.jpg)',
            opacity: 0.15
          }}
        />
        <div className="absolute inset-0 bg-black/60" />
      </div>

      {/* Hamburger Menu */}
      <Button
        onClick={() => setMenuOpen(!menuOpen)}
        className="fixed top-6 left-6 z-50 w-14 h-14 p-0 rounded-xl bg-white/90 backdrop-blur-xl border-2 border-white shadow-2xl hover:bg-white hover:scale-110 hover:shadow-[0_0_30px_rgba(255,255,255,0.5)] transition-all duration-300"
        style={{ boxShadow: '0 0 20px rgba(255, 255, 255, 0.3), 0 8px 24px rgba(0, 0, 0, 0.5)' }}
      >
        {menuOpen ? <X className="h-7 w-7 text-black" /> : <Menu className="h-7 w-7 text-black" />}
      </Button>

      {/* Side Menu */}
      <div className={`fixed top-0 left-0 h-full w-80 bg-black/95 backdrop-blur-xl border-r border-white/10 z-40 transition-transform duration-500 ease-in-out ${menuOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="p-8 pt-24">
          <div className="mb-8">
            <h2 className="text-2xl font-black text-white mb-2">MENU</h2>
            <p className="text-gray-400 text-sm">Navigate your dashboard</p>
          </div>

          <div className="space-y-3">
            <Link to="/home" className="menu-item block">
              <div className="group flex items-center gap-4 p-4 rounded-xl border border-white/10 bg-white/5 hover:bg-white hover:scale-105 transition-all duration-300 cursor-pointer">
                <Home className="h-6 w-6 text-white group-hover:text-black transition-colors duration-300" />
                <span className="text-white group-hover:text-black font-semibold transition-colors duration-300">Home</span>
              </div>
            </Link>

            <Link to="/markets" className="menu-item block">
              <div className="group flex items-center gap-4 p-4 rounded-xl border border-white/10 bg-white hover:scale-105 transition-all duration-300 cursor-pointer">
                <BarChart className="h-6 w-6 text-black transition-colors duration-300" />
                <span className="text-black font-semibold transition-colors duration-300">Markets</span>
              </div>
            </Link>

            {!isGuest && (
              <Link to="/profile" className="menu-item block">
                <div className="group flex items-center gap-4 p-4 rounded-xl border border-white/10 bg-white/5 hover:bg-white hover:scale-105 transition-all duration-300 cursor-pointer">
                  <User className="h-6 w-6 text-white group-hover:text-black transition-colors duration-300" />
                  <span className="text-white group-hover:text-black font-semibold transition-colors duration-300">Profile</span>
                </div>
              </Link>
            )}

            <button onClick={handleLogout} className="menu-item w-full">
              <div className="group flex items-center gap-4 p-4 rounded-xl border border-white/10 bg-white/5 hover:bg-red-500 hover:scale-105 transition-all duration-300 cursor-pointer">
                <LogOut className="h-6 w-6 text-white transition-colors duration-300" />
                <span className="text-white font-semibold transition-colors duration-300">Logout</span>
              </div>
            </button>
          </div>

          <div className="mt-8 p-4 rounded-xl border border-white/10 bg-white/5">
            <p className="text-xs text-gray-400 mb-1">{isGuest ? 'Demo Balance' : 'Your Balance'}</p>
            <p className="text-2xl font-black text-white">${user?.balance || 0}</p>
          </div>
        </div>
      </div>

      {/* Overlay */}
      {menuOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-30 backdrop-blur-sm"
          onClick={() => setMenuOpen(false)}
        />
      )}

      <div className="container py-4 space-y-4 relative">
        <div className="flex items-center justify-between gap-3 mb-4 ml-16">
          <div className="flex items-center gap-4">
            <div className="p-4 rounded-2xl bg-gradient-to-br from-white/20 to-white/10 backdrop-blur-md border-2 border-white/30 shadow-2xl hover:scale-110 transition-transform duration-300"
              style={{ boxShadow: '0 0 30px rgba(255, 255, 255, 0.2), 0 8px 32px rgba(0, 0, 0, 0.6)' }}>
              <TrendingUp className="h-10 w-10 text-white drop-shadow-[0_0_10px_rgba(255,255,255,0.8)]" />
            </div>
            <div>
              <h1 className="text-6xl font-black text-white tracking-tight drop-shadow-[0_0_20px_rgba(255,255,255,0.5)]"
                style={{ textShadow: '0 0 30px rgba(255, 255, 255, 0.5), 0 4px 12px rgba(0, 0, 0, 0.8), 2px 2px 0px rgba(255, 255, 255, 0.1)' }}>
                MARKETS
              </h1>
              <p className="text-gray-300 text-base font-medium drop-shadow-md">
                Trade on real-world events
              </p>
            </div>
          </div>
          <div className="flex gap-3">
            <Button
              onClick={() => setShowAnalytics(!showAnalytics)}
              className={`h-14 px-8 text-base font-bold transition-all hover:scale-110 rounded-xl ${
                showAnalytics
                  ? 'bg-white text-black hover:bg-gray-100 shadow-[0_0_25px_rgba(255,255,255,0.4)]'
                  : 'bg-white/20 text-white border-2 border-white/40 hover:bg-white hover:text-black shadow-[0_0_20px_rgba(255,255,255,0.2)]'
              }`}
              style={{
                boxShadow: showAnalytics
                  ? '0 0 25px rgba(255, 255, 255, 0.4), 0 8px 24px rgba(0, 0, 0, 0.5)'
                  : '0 0 20px rgba(255, 255, 255, 0.2), 0 8px 24px rgba(0, 0, 0, 0.4)'
              }}
            >
              <BarChart3 className="mr-2 h-6 w-6" />
              Analytics
            </Button>
          </div>
        </div>

        <Card className="group p-7 border-2 border-white/20 bg-black/85 backdrop-blur-xl hover:bg-black/95 hover:border-white/40 transition-all duration-500 shadow-2xl"
          style={{ boxShadow: '0 0 40px rgba(0, 0, 0, 0.8), 0 8px 32px rgba(0, 0, 0, 0.6)' }}>
          <div className="flex flex-col gap-4 md:flex-row md:items-center">
            <div className="relative flex-1">
              <Search className="absolute left-5 top-1/2 h-6 w-6 -translate-y-1/2 text-gray-300 group-hover:text-white transition-colors duration-300 drop-shadow-lg" />
              <Input
                placeholder="Search markets..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-14 h-14 text-base border-2 border-white/30 bg-black/60 text-white placeholder:text-gray-400 focus:border-white focus:bg-black/80 focus:shadow-[0_0_20px_rgba(255,255,255,0.2)] transition-all duration-300 rounded-xl font-semibold"
              />
            </div>
            <div className="flex gap-3 flex-wrap">
              <Button
                variant={category === '' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setCategory('')}
                className={`h-14 px-6 text-base font-bold transition-all hover:scale-110 rounded-xl ${
                  category === ''
                    ? 'bg-white text-black hover:bg-gray-100 shadow-[0_0_25px_rgba(255,255,255,0.4)]'
                    : 'border-2 border-white/40 bg-black/60 text-white hover:bg-white hover:text-black shadow-[0_0_15px_rgba(255,255,255,0.15)]'
                }`}
                style={{
                  boxShadow: category === ''
                    ? '0 0 25px rgba(255, 255, 255, 0.4), 0 6px 20px rgba(0, 0, 0, 0.5)'
                    : '0 0 15px rgba(255, 255, 255, 0.15), 0 6px 20px rgba(0, 0, 0, 0.4)'
                }}
              >
                <Filter className="mr-2 h-5 w-5" />
                All
              </Button>
              {categories.map((cat) => (
                <Button
                  key={cat}
                  variant={category === cat ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setCategory(cat)}
                  className={`h-14 px-6 text-base font-bold transition-all hover:scale-110 rounded-xl ${
                    category === cat
                      ? 'bg-white text-black hover:bg-gray-100 shadow-[0_0_25px_rgba(255,255,255,0.4)]'
                      : 'border-2 border-white/40 bg-black/60 text-white hover:bg-white hover:text-black shadow-[0_0_15px_rgba(255,255,255,0.15)]'
                  }`}
                  style={{
                    boxShadow: category === cat
                      ? '0 0 25px rgba(255, 255, 255, 0.4), 0 6px 20px rgba(0, 0, 0, 0.5)'
                      : '0 0 15px rgba(255, 255, 255, 0.15), 0 6px 20px rgba(0, 0, 0, 0.4)'
                  }}
                >
                  {cat}
                </Button>
              ))}
            </div>
          </div>
        </Card>

        {!data || data.data.length === 0 ? (
          <Card className="p-12 text-center border-2 border-white/10 bg-black/80 backdrop-blur-md hover:border-white/30 transition-all duration-500 shadow-2xl">
            <div className="flex flex-col items-center gap-6">
              <img
                src="https://github.githubassets.com/images/mona-loading-dark.gif"
                alt="Loading..."
                className="w-32 h-32"
              />
              <div>
                <p className="text-white font-black text-2xl mb-2">LOADING MARKETS</p>
                <p className="text-gray-400 text-base font-light">Fetching latest prediction markets...</p>
              </div>
            </div>
          </Card>
        ) : null}

        {data && data.data.length > 0 && !showAnalytics && (
          <div>
            <div className="mb-6 flex items-center justify-between">
              <div>
                <p className="text-white text-sm font-light text-gray-400">
                  Showing {data.data.length} markets
                </p>
              </div>
            </div>
            <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
              {data.data.map((market, index) => (
                <div
                  key={market.id}
                  className="animate-fadeIn"
                  style={{ animationDelay: `${index * 0.1}s` }}
                >
                  <MarketCard market={market} />
                </div>
              ))}
            </div>
          </div>
        )}

        {data && data.data.length > 0 && showAnalytics && (
          <div className="space-y-6">
            <Card className="p-8 border-2 border-white/10 bg-black/80 backdrop-blur-md shadow-2xl">
              <h3 className="text-3xl font-black text-white mb-6 tracking-tight">MARKET ANALYTICS</h3>

              <div className="grid grid-cols-3 gap-6 mb-8">
                <div className="p-6 rounded-2xl border-2 border-white/10 bg-white/5 hover:bg-white/10 transition-all duration-300">
                  <p className="text-gray-400 text-sm font-medium mb-2">TOTAL VOLUME</p>
                  <p className="text-4xl font-black text-white">
                    ${data.data.reduce((sum, m) => sum + m.totalVolume, 0).toLocaleString()}
                  </p>
                </div>
                <div className="p-6 rounded-2xl border-2 border-white/10 bg-white/5 hover:bg-white/10 transition-all duration-300">
                  <p className="text-gray-400 text-sm font-medium mb-2">ACTIVE MARKETS</p>
                  <p className="text-4xl font-black text-white">
                    {data.data.filter(m => m.status === 'active').length}
                  </p>
                </div>
                <div className="p-6 rounded-2xl border-2 border-white/10 bg-white/5 hover:bg-white/10 transition-all duration-300">
                  <p className="text-gray-400 text-sm font-medium mb-2">AVG PROBABILITY</p>
                  <p className="text-4xl font-black text-white">
                    {data.data.length > 0 && data.data[0].options?.length
                      ? Math.round(data.data.reduce((sum, m) => sum + (m.options?.[0]?.probability || 0), 0) / data.data.length * 100)
                      : 0}%
                  </p>
                </div>
              </div>

              <div className="space-y-6">
                <h4 className="text-2xl font-bold text-white mb-4">TOP PERFORMING MARKETS</h4>
                {data.data.slice(0, 5).map((market, index) => (
                  <div
                    key={market.id}
                    className="group p-6 rounded-2xl border-2 border-white/10 bg-white/5 hover:bg-white hover:scale-105 transition-all duration-500 cursor-pointer"
                    onClick={() => navigate(`/markets/${market.id}`)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-4 mb-2">
                          <span className="text-2xl font-black text-white group-hover:text-black transition-colors duration-500">
                            #{index + 1}
                          </span>
                          <h5 className="text-xl font-bold text-white group-hover:text-black transition-colors duration-500">
                            {market.title}
                          </h5>
                        </div>
                        <p className="text-gray-400 group-hover:text-black text-sm font-medium transition-colors duration-500">
                          {market.category} • Resolves {new Date(market.resolveDate).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-3xl font-black text-white group-hover:text-black transition-colors duration-500">
                          ${market.totalVolume.toLocaleString()}
                        </p>
                        <p className="text-sm text-gray-400 group-hover:text-black font-medium transition-colors duration-500">
                          Volume
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </Card>
          </div>
        )}
      </div>
    </div>
  )
}
